import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import LoadingSpinner from "../../Components/Loading/LoadingSpinner";
import {
  fetchMarketOrderDetail,
  clearOrderDetail,
} from "../../store/actions/MarketSlice";

const STATUS_COLORS = {
  pending: "#f5a623",
  ready_to_ship: "#7575ff",
  shipped: "#4e14a5",
  delivered: "#01b574",
  declined: "#ff1733",
  return_to_seller: "#e31a1a",
  cancelled: "#a0aec0",
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || "#a0aec0";
  return (
    <span
      className="text-uppercase fw-bold"
      style={{
        color,
        border: `1px solid ${color}`,
        borderRadius: "12px",
        padding: "2px 10px",
        fontSize: "0.85rem",
      }}
    >
      {status?.replaceAll("_", " ")}
    </span>
  );
}

const TIMESTAMP_FIELDS = [
  { key: "shipped_at", label: "Shipped At" },
  { key: "delivered_at", label: "Delivered At" },
  { key: "cancelled_at", label: "Cancelled At" },
  { key: "declined_at", label: "Declined At" },
  { key: "returned_at", label: "Returned At" },
];

const ADDRESS_FIELDS = ["street", "city", "state", "country", "postal_code", "zip"];

function AddressSnapshot({ address }) {
  if (!address || Object.keys(address).length === 0) {
    return <p className="text-grey mb-0">—</p>;
  }

  const knownFields = ADDRESS_FIELDS.filter((f) => address[f]);

  if (knownFields.length > 0) {
    return (
      <div>
        {knownFields.map((f) => (
          <p key={f} className="mb-1">
            <span className="text-grey text-capitalize">{f}: </span>
            {address[f]}
          </p>
        ))}
      </div>
    );
  }

  return (
    <pre
      className="text-white mb-0"
      style={{
        backgroundColor: "#2d2d2d",
        padding: "12px",
        borderRadius: "8px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {JSON.stringify(address, null, 2)}
    </pre>
  );
}

export default function MarketOrderDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { orderDetail, orderDetailLoading } = useSelector((state) => state.market);

  useEffect(() => {
    dispatch(fetchMarketOrderDetail(id));
    return () => {
      dispatch(clearOrderDetail());
    };
  }, [dispatch, id]);

  if (orderDetailLoading) {
    return <LoadingSpinner />;
  }

  if (!orderDetail) {
    return (
      <div className="text-center text-grey mt-5">
        <h5>No data yet</h5>
      </div>
    );
  }

  const order = orderDetail;
  const presentTimestamps = TIMESTAMP_FIELDS.filter((f) => order?.[f.key]);

  return (
    <div className="text-white" data-aos="fade-up" data-aos-duration="600">
      <div className="d-flex align-items-center gap-3 mb-4">
        <h3 className="fw-bold mb-0">Order #{order?.id}</h3>
        <StatusBadge status={order?.status} />
      </div>

      <div className="card-performance rounded-20 p-4 mb-4">
        <p className="mb-1">
          <span className="text-grey">Total: </span>
          {order?.total_amount} {order?.currency}
        </p>
        {order?.tracking_number && (
          <p className="mb-1">
            <span className="text-grey">Tracking Number: </span>
            {order.tracking_number}
          </p>
        )}
        <p className="mb-1">
          <span className="text-grey">Auto Confirmed: </span>
          {order?.auto_confirmed ? "Yes" : "No"}
        </p>
        <p className="mb-0">
          <span className="text-grey">Created: </span>
          {order?.created_at
            ? new Date(order.created_at * 1000).toLocaleString()
            : "—"}
        </p>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="card-performance rounded-20 p-4 mb-4">
            <p className="text-white fs-5 fw-bold mb-3">Store</p>
            <div className="d-flex align-items-center gap-3">
              {order?.store?.logo_url && (
                <img
                  src={order.store.logo_url}
                  alt="store logo"
                  width={50}
                  height={50}
                  style={{ objectFit: "cover", borderRadius: "8px" }}
                />
              )}
              <p className="mb-0">{order?.store?.name || "—"}</p>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card-performance rounded-20 p-4 mb-4">
            <p className="text-white fs-5 fw-bold mb-3">Buyer</p>
            <div className="d-flex align-items-center gap-3">
              {order?.buyer?.img && (
                <img
                  src={order.buyer.img}
                  alt="buyer"
                  width={50}
                  height={50}
                  style={{ objectFit: "cover", borderRadius: "50%" }}
                />
              )}
              <div>
                <p className="mb-0">{order?.buyer?.name ? `@${order.buyer.name}` : "—"}</p>
                <p className="text-grey mb-0">{order?.buyer?.nick_name || "—"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {presentTimestamps.length > 0 && (
        <div className="card-performance rounded-20 p-4 mb-4">
          <p className="text-white fs-5 fw-bold mb-3">Timeline</p>
          {presentTimestamps.map((f) => (
            <p key={f.key} className="mb-1">
              <span className="text-grey">{f.label}: </span>
              {new Date(order[f.key] * 1000).toLocaleString()}
            </p>
          ))}
        </div>
      )}

      <div className="card-performance rounded-20 p-4 mb-4">
        <p className="text-white fs-5 fw-bold mb-3">Shipping Address</p>
        <AddressSnapshot address={order?.address_snapshot} />
      </div>

      {order?.notes && (
        <div className="card-performance rounded-20 p-4 mb-4">
          <p className="text-white fs-5 fw-bold mb-2">Notes</p>
          <p className="mb-0">{order.notes}</p>
        </div>
      )}

      <div className="card-performance rounded-20 p-4 mb-4">
        <p className="text-white fs-5 fw-bold mb-3">Items</p>
        {order?.items?.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table className="w-100 text-white">
              <thead>
                <tr className="text-grey">
                  <th className="text-start py-2">Product</th>
                  <th className="text-start py-2">Qty</th>
                  <th className="text-start py-2">Price</th>
                  <th className="text-start py-2">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => (
                  <tr key={item.id} style={{ borderTop: "#56577A 1px solid" }}>
                    <td className="py-2 d-flex align-items-center gap-2">
                      {item?.product?.thumbnail_url && (
                        <img
                          src={item.product.thumbnail_url}
                          alt="product"
                          width={36}
                          height={36}
                          style={{ objectFit: "cover", borderRadius: "6px" }}
                        />
                      )}
                      {item?.product?.name || "—"}
                    </td>
                    <td className="py-2">{item?.quantity}</td>
                    <td className="py-2">{item?.price_at_purchase}</td>
                    <td className="py-2">{item?.subtotal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-grey mb-0">No data yet</p>
        )}
      </div>
    </div>
  );
}
